var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import $ from 'jquery';
import { Map, View } from "ol";
import { addProjection, get as getProjection, transform, transformExtent } from 'ol/proj';
import Projection from "ol/proj/Projection";
import proj4 from "proj4";
import { LabelLayer } from "../../impl/layers/server/LabelLayer";
import { Switchboard } from "../events";
import { ReadyMessage } from "../messages";
import { WaitFor } from "../misc/decorators";
import { Maybe, None, Some } from "../misc/option";
import { EPSG } from "../projection";
import { LayerBox } from "../ui/layer";
export class ExtendedMap extends Map {
    constructor(options) {
        super(options);
        this.rest_layers = [];
        this.projection = None.instance();
        this.is_loaded = false;
        this.receiver = Switchboard.get_receiver("MOVEMENT");
        this.base_layer_change_receiver = Switchboard.get_receiver("BASE_LAYER_CHANGED");
        this.zoom_change_receiver = Switchboard.get_receiver("MAP_ZOOM_EVENT");
        this.selection_layer = None.instance();
        this.default_extent = None.instance();
        this.strategy = options.strategy;
        this.strategy.create_layers().then(async (layers) => {
            var _a, _b;
            this.rest_layers = layers;
            this.projection = this.base_layer.map(sl => sl.projection);
            this.layer_box = new LayerBox(this.rest_layers);
            layers.forEach(l => {
                if (l instanceof LabelLayer) {
                    let ll = l;
                    if (!ll.is_base && ll.visible) {
                        return;
                    }
                }
                l.layers.map(r => {
                    try {
                        this.addLayer(r);
                    }
                    catch (_a) {
                        throw new Error("Failed to add layer.");
                    }
                });
            });
            let parcel_query_extent = null;
            if (this.base_layer.isSome()) {
                parcel_query_extent = await this.base_layer.unwrap().get_query_service_extent((_b = (_a = this.getStrategy()) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.ParcelQueryUrl);
            }
            try {
                this.create_view(this.strategy.min_resolution, this.strategy.max_resolution, parcel_query_extent !== null && parcel_query_extent !== void 0 ? parcel_query_extent : null);
            }
            catch (err) {
                throw new Error(`${err}. Could not create view.`);
            }
            this.updateSize();
            this.changed();
            this.initialize_handlers();
            this.setup_zoom_listeners();
            this.setup_base_layer_changed_listener();
            if (this.rest_layers.filter(l => l.is_base && l.visible).length > 1) {
                let layer = [...this.rest_layers].reverse().find(l => l.is_base && l.visible);
                let broadcaster = Switchboard.get_emitter("BASE_LAYER_CHANGED");
                broadcaster.emit('base_layer_changed', layer);
            }
            this.is_loaded = true;
        });
        this.receiver.on("center", (maybe_coord) => {
            maybe_coord.map((c) => this.view.animate({ center: c, duration: 200 }));
        });
    }
    get base_layer() {
        let first_base_layer = this.rest_layers.find(l => l.is_base && l.visible && !l.parsing_failed);
        if (!first_base_layer) {
            first_base_layer = this.rest_layers.find(l => l.is_base && !l.parsing_failed);
            if (!first_base_layer) {
                throw Error("No base layer defined in config. Cannot continue.");
            }
            else {
                return new Some(first_base_layer);
            }
        }
        return new Some(first_base_layer);
    }
    get view() { return this.getView(); }
    get viewport() { return this.getViewport(); }
    get size() { return this.getSize(); }
    getStrategy() { return JSON.parse(JSON.stringify(this.strategy)); }
    async create_view(min_res, max_res, parcel_query_extent) {
        var _a, _b, _c;
        const min_res_divisor = (_c = (_b = (_a = this.getStrategy().config) === null || _a === void 0 ? void 0 : _a.Zoom) === null || _b === void 0 ? void 0 : _b.MinResolutionDivisor) !== null && _c !== void 0 ? _c : 1;
        this.setView(new View({
            projection: this.projection.unwrapOr(EPSG(4426)),
            zoom: 12,
            minResolution: min_res / min_res_divisor,
            maxResolution: max_res,
            center: [0, 0]
        }));
        if (parcel_query_extent) {
            const queryExtent = [parcel_query_extent.xmin, parcel_query_extent.ymin, parcel_query_extent.xmax, parcel_query_extent.ymax];
            let newExtent;
            if (parcel_query_extent.spatialReference.latestWkid) {
                newExtent = transformExtent(queryExtent, EPSG(parcel_query_extent.spatialReference.latestWkid), this.getView().getProjection());
            }
            else {
                const customProjectionCode = 'EPSG:CUSTOM_WKT';
                proj4.defs(customProjectionCode, parcel_query_extent.spatialReference.wkt);
                addProjection(new Projection({
                    code: customProjectionCode,
                }));
                newExtent = transformExtent(queryExtent, getProjection(customProjectionCode), this.getView().getProjection());
            }
            this.default_extent = new Some(newExtent);
            this.getView().fit(newExtent, { size: this.size, padding: [80, 80, 80, 80] });
        }
        else {
            console.error(`Error setting parcel query extent. Switching to base layer extent.`);
            this.set_base_layer_extent();
        }
    }
    set_base_layer_extent() {
        this.base_layer
            .flatmap(bl => bl.raw)
            .flatmap(l => Maybe(l.getExtent()))
            .map(e => this.view.fit(e, { size: this.size, padding: [80, 80, 80, 80] }));
    }
    initialize_handlers() {
        this.viewport.addEventListener('mouseout', (e) => $('#help-message').addClass('hidden'));
    }
    get_default_extent() {
        return this.default_extent;
    }
    get_highest_z() {
        const layers = this.getLayers().getArray();
        return Math.max(...layers.map(l => l.getZIndex())) || 0;
    }
    async loaded() {
        while (!this.is_loaded) {
            await new Promise(res => setTimeout(res, 20));
        }
        if (!$('body').hasClass('loaded')) {
            $('body').addClass('loaded');
            $("#loader").addClass("hidden");
        }
        return this;
    }
    async add_client_layers(...client_layers) {
        await this.loaded();
        for (let layer of client_layers) {
            const z = this.get_highest_z();
            layer.projection = this.projection.unwrapOr(EPSG(3857));
            layer.raw.setZIndex(z + 1);
            this.addLayer(layer.raw);
        }
    }
    async add_selection_layer(selection_layer) {
        if (this.selection_layer.isNone()) {
            this.selection_layer = new Some(selection_layer);
        }
        await this.loaded();
        const z = this.get_highest_z();
        selection_layer.raw.setZIndex(z + 1);
        selection_layer.projection = this.projection.unwrapOr(EPSG(4426));
        this.addLayer(selection_layer.raw);
    }
    setup_base_layer_changed_listener() {
        this.base_layer_change_receiver.on("base_layer_changed", async (layer) => {
            let current_proj = this.projection.unwrapOr(EPSG(4426)).split(":").pop();
            let new_proj;
            if (this.rest_layers.filter(l => l.is_base && l.visible).length > 1) {
                let layer = [...this.rest_layers].reverse().find(l => l.is_base && l.visible);
                new_proj = layer === null || layer === void 0 ? void 0 : layer.projection.split(":").pop();
            }
            else {
                if (layer.visible) {
                    new_proj = layer.projection.split(":").pop();
                }
                else
                    return;
            }
            if (current_proj !== new_proj) {
                this.projection = new Some(layer.projection);
                try {
                    const view = this.getView();
                    const currentProjection = view.getProjection();
                    const currentCenter = view.getCenter();
                    const currentExtent = view.calculateExtent(this.getSize());
                    const newCenter = transform(currentCenter, currentProjection, layer.projection);
                    const newExtent = transformExtent(currentExtent, currentProjection, layer.projection);
                    const newView = new View({
                        projection: layer.projection,
                        center: newCenter,
                        zoom: view.getZoom(),
                    });
                    this.setView(newView);
                    newView.fit(newExtent, { size: this.getSize() });
                    this.updateSize();
                    this.changed();
                    if (this.selection_layer.isSome()) {
                        let s_layer = this.selection_layer.unwrap();
                        let ids = s_layer.features.map(x => x.get(s_layer.identifier));
                        let data = s_layer.features.map(x => x.getProperties());
                        let focused = s_layer.clicked;
                        s_layer.clear();
                        this.removeLayer(s_layer.raw);
                        await this.add_selection_layer(s_layer);
                        await s_layer.add_features_by_ids(ids, focused);
                        for (let parcel of s_layer.features) {
                            const parcelStrap = parcel.get(s_layer.identifier);
                            const record = data.find(item => item[s_layer.identifier] === parcelStrap);
                            if (record) {
                                let geometry = record.geometry;
                                if (geometry) {
                                    geometry = geometry.transform(currentProjection, layer.projection);
                                    for (let key in record) {
                                        parcel.set(key, record[key]);
                                    }
                                }
                            }
                        }
                    }
                }
                catch (err) {
                    throw new Error(`${err}. Could not create view on base layer change.`);
                }
            }
        });
    }
    setup_zoom_listeners() {
        this.zoom_change_receiver.on('map_zoom_event', async (event) => {
            var _a, _b;
            if (event === 'zoom-in') {
                let current_zoom = (_a = this.getView().getZoom()) !== null && _a !== void 0 ? _a : 100;
                let max_zoom = this.getView().getMaxZoom();
                if (current_zoom < max_zoom) {
                    this.getView().setZoom(current_zoom + 1);
                }
            }
            else {
                let current_zoom = (_b = this.getView().getZoom()) !== null && _b !== void 0 ? _b : 100;
                let min_zoom = this.getView().getMinZoom();
                if (current_zoom > min_zoom) {
                    this.getView().setZoom(current_zoom - 1);
                }
            }
        });
    }
    get_features_at_pixel(pixel) {
        let hit = this.hasFeatureAtPixel(pixel);
        let features = [];
        if (hit) {
            this.forEachFeatureAtPixel(pixel, (feature, layer) => {
                features.push([feature, layer]);
            });
        }
        return features;
    }
    ready() {
        ReadyMessage.send();
    }
    add_layerbox_components(...components) {
        var _a;
        (_a = this.layer_box) === null || _a === void 0 ? void 0 : _a.add_components(...components);
    }
}
__decorate([
    WaitFor()
], ExtendedMap.prototype, "loaded", null);
